import { flattenDepth, range } from 'lodash';
import * as queryString from 'query-string';

import {
  APIException,
  baseUrl,
  del,
  get,
  getWithHeaders,
  patch,
  post,
  put,
  sendFormData,
} from '../api/apiCore';
import { AuthRecovery } from '../api/AuthRecovery';
import { CORVA_API_URLS } from '../constants';
import { getAuthorizationHeaders } from '../utils';

import { mockAssetsData } from './mocks/mockAssetData';

export * from './postTaskAndWaitResult';

const warnDeprecated = ({ method = 'unknown', alternative } = {}) => {
  if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
    console.warn(
      `You are using a deprecated "${method}" method from "@corva/ui/clients/jsonApi".${
        alternative ? ` Consider using "${alternative}".` : ''
      }`
    );
  }
};

/**
 * Fetches the current user from the API.
 * Auth flow uses access token (authorization token) and refresh token.
 * Bothe the access token and refresh token are stored in the same storage:
 * - Web platform: in the HTTP-only cookie.
 * - Mobile app: iOS Keychain
 *
 * The token refresh mechanism is based on exchanging the refresh token for a new access token.
 * The refresh token is sent with the request to the /v1/user_token endpoint for both Web platform and Mobile app:
 * - Web platform: in the HTTP-only cookie using 'credentials: 'include''.
 * - Mobile app: in request headers using 'Authorization' header.
 */
async function fetchCurrentUser() {
  const response = await fetch(`${baseUrl}/v1/users/current`, {
    credentials: 'include',
    headers: { ...getAuthorizationHeaders() },
  });
  let json;
  try {
    json = await response.json();
  } catch (e) {
    json = {};
  }
  return { response, json };
}

async function refreshAndRetry(originalException) {
  try {
    // Attempt to refresh authorization token in HTTP-only cookie.
    await AuthRecovery.getInstance().tryRefresh();
    // Pick up the updated HTTP-only cookie with the new authorization token.
    const { response, json } = await fetchCurrentUser();
    if (!response?.ok) {
      throw new APIException(response, json);
    }
    return json;
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw originalException;
  }
}

/**
 * Fetches the current user from the API and checks if the user is logged in.
 * If the authorization token is expired, it will attempt to refresh the token and retry the request.
 * If the refresh fails, it will throw the original exception.
 */
export async function getCurrentUser() {
  const { response, json } = await fetchCurrentUser();

  if (response?.ok) {
    return json;
  }

  const exception = new APIException(response, json);

  if (AuthRecovery.getInstance().isRefreshableAuthError(exception)) {
    return refreshAndRetry(exception);
  }

  throw exception;
}

export async function getWellViewBha(options = {}) {
  return get('/v2/integration/wellview/bha', options);
}

export async function getWells(options = {}, getterFn = get) {
  return getterFn('/v2/wells', options);
}

export async function getWell(id, options = {}) {
  return get(`/v2/wells/${id}`, options);
}

export async function getRig(id, options = {}) {
  return get(`/v2/rigs/${id}`, options);
}

export async function patchRig(id, data, fields = []) {
  return patch(`/v2/rigs/${id}`, data, { fields });
}

export async function getRigs(options = {}, getterFn = get) {
  return getterFn('/v2/rigs', options);
}

export async function getPrograms(options = {}, getterFn = get) {
  return getterFn('/v2/programs', options);
}

export async function getInterventionUnits(options = {}, getterFn = get) {
  return getterFn('/v2/intervention_units', options);
}

export async function getInterventionUnit(interventionUnitId, options = {}, getterFn = get) {
  return getterFn(`/v2/intervention_units/${interventionUnitId}`, options);
}

export function getInterventionUnitEvents(options = {}) {
  return get('/v2/intervention_unit_events', options);
}

export async function getAsset(id, options = {}) {
  return get(`/v2/assets/${id}`, options);
}

export async function getAssets(options = {}, getterFn = get) {
  return getterFn('/v2/assets', options);
}

export async function getResolvedAssets(options = {}) {
  return post('/v2/assets/resolve', options);
}

export async function getPads(options = {}, getterFn = get) {
  return getterFn('/v2/pads', options);
}

export async function getPad(id, options = {}, getterFn = get) {
  return getterFn(`/v2/pads/${id}`, options);
}

export async function getFracFleet(id, options = {}, getterFn = get) {
  return getterFn(`/v2/frac_fleets/${id}`, options);
}

export async function getFracFleets(options = {}, getterFn = get) {
  return getterFn('/v2/frac_fleets', options);
}

export async function getDrilloutUnit(drilloutUnitId, options = {}) {
  return get(`/v2/drillout_units/${drilloutUnitId}`, options);
}

export async function getDrilloutUnits(options = {}, getterFn = get) {
  return getterFn('/v2/drillout_units', options);
}

/**
 * @deprecated
 * @param provider
 * @param collection
 * @param queryParams
 * @returns {Promise<*>}
 */
export async function deleteAppStorageRecords(provider, collection, queryParams) {
  warnDeprecated({ method: 'deleteAppStorageRecords' });
  return del(`/v1/data/${provider}/${collection}`, queryParams);
}

/**
 * @deprecated
 * @param provider
 * @param collection
 * @param id
 * @returns {Promise<*>}
 */
export async function deleteAppStorage(provider, collection, id) {
  warnDeprecated({ method: 'deleteAppStorage' });
  return del(`/v1/data/${provider}/${collection}/${id}`);
}

/**
 * @deprecated
 * @param provider
 * @param collection
 * @param assetId
 * @param params
 * @returns {Promise<*>}
 */
export async function getAppStorage(provider, collection, assetId, params = {}) {
  warnDeprecated({ method: 'getAppStorage', alternative: 'getDataAppStorage' });
  return get(
    `/v1/data/${provider}/${collection}`,
    { ...params, asset_id: assetId },
    { appKey: params.appKey }
  );
}

export async function getDataAppStorage(provider, collection, params = {}) {
  return get(`/api/v1/data/${provider}/${collection}/`, params, {
    apiUrl: CORVA_API_URLS.DATA_API,
  });
}

export async function getDataAppStorageAggregate(provider, collection, params = {}) {
  return get(`/api/v1/data/${provider}/${collection}/aggregate/`, params, {
    apiUrl: CORVA_API_URLS.DATA_API,
  });
}

/**
 * @deprecated
 * @param provider
 * @param collection
 * @param id
 * @param item
 * @param queryParams
 * @returns {Promise<*>}
 */
export async function putAppStorage(provider, collection, id, item, queryParams = {}) {
  warnDeprecated({ method: 'putAppStorage', alternative: 'putDataAppStorage' });
  return put(`/v1/data/${provider}/${collection}/${id}`, item, queryParams);
}

export async function putDataAppStorage(provider, collection, id, item, queryParams) {
  return put(`/api/v1/data/${provider}/${collection}/${id}`, item, queryParams, {
    apiUrl: CORVA_API_URLS.DATA_API,
  });
}

/**
 * @deprecated
 * @param provider
 * @param collection
 * @param item
 * @returns {Promise<*>}
 */
export async function postAppStorage(provider, collection, item) {
  warnDeprecated({ method: 'postAppStorage', alternative: 'postDataAppStorage' });
  return post(`/v1/data/${provider}/${collection}`, item);
}

export async function postDataAppStorage(provider, collection, item) {
  return post(`/api/v1/data/${provider}/${collection}`, item, {
    apiUrl: CORVA_API_URLS.DATA_API,
  });
}

export async function getCompetitorAnalysisData(options) {
  return get('/v1/competitor_analysis', options);
}

export async function getCompanies() {
  return get('/v1/companies');
}

export async function getPicklist(name) {
  return get(`/v2/picklists/${name}`);
}

export async function getFavorites() {
  return get(`/v2/favorites`);
}

export async function getCompanyGoals(companyId) {
  return get(`/v1/companies/${companyId}/settings/company_goals`);
}

export const getLASFileList = async assetId => {
  const match = JSON.stringify({ asset_id: +assetId });
  const group = JSON.stringify({
    _id: '$data.file',
    name: { $first: '$data.file' },
    timestamp: { $first: '$timestamp' },
  });
  const sort = JSON.stringify({ timestamp: -1 });

  try {
    return await getDataAppStorageAggregate('corva', 'formation-evaluation.metadata', {
      match,
      group,
      sort,
      limit: 1_000,
    });
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getLASMetadata = async (assetId, fileName) => {
  try {
    return await getDataAppStorage('corva', 'formation-evaluation.metadata', {
      query: JSON.stringify({ asset_id: +assetId, 'data.file': fileName }),
      sort: JSON.stringify({ timestamp: -1 }),
      limit: 1,
    });
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getLasFileDepthData = async (assetId, fileName, page, numberOfRows) => {
  const match = JSON.stringify({ asset_id: +assetId, 'metadata.file': fileName });
  const sort = JSON.stringify({ 'data.md': 1 });

  return getDataAppStorageAggregate('corva', 'formation-evaluation.data', {
    match,
    sort,
    skip: numberOfRows * (page - 1),
    limit: numberOfRows,
  });
};

export const getLasFileTimeData = async (assetId, fileName, page, numberOfRows) => {
  const params = {
    query: JSON.stringify({ 'metadata.asset_id': +assetId, 'metadata.file': fileName }),
    sort: JSON.stringify({ datetime: 1 }),
    skip: numberOfRows * (page - 1),
    limit: numberOfRows,
  };

  return getDataAppStorage('corva', 'logs-importer.data.time', params);
};

export const getAllLASFileData = async ({
  assetId,
  fileName,
  recordsCount,
  fileKind = 'depth',
}) => {
  const limit = 10_000;

  const pagesCount = Math.ceil(recordsCount / limit);
  const getFileDataFn = fileKind === 'depth' ? getLasFileDepthData : getLasFileTimeData;

  const promises = range(pagesCount).map(page => getFileDataFn(assetId, fileName, page + 1, limit));

  return Promise.all(promises).then(responses => flattenDepth(responses));
};

export const getStreams = ({ assetId, segment = 'drilling' }) => {
  return get(`/v1/app_streams?asset_id=${assetId}&segment=${segment}`);
};

/** higher order function that returns request function based on provided assetType
 * @param {string} assetType - any asset type currently supported in
 * @param {boolean} shouldFetchHeaders - if true, fetch headers object will be returned
 * ~/constants/assets
 * */
export function mapAssetGetRequest(assetType, shouldFetchHeaders = false) {
  if (process.env.IS_STORYBOOK) {
    return mockAssetsData(assetType, shouldFetchHeaders);
  }

  const withProperGetter =
    fn =>
    (...params) =>
      shouldFetchHeaders ? fn(...params, getWithHeaders) : fn(...params);

  const requestMap = {
    rig: getRigs,
    well: getWells,
    frac_fleet: getFracFleets,
    drillout_unit: getDrilloutUnits,
    pad: getPads,
    program: getPrograms,
    intervention_unit: getInterventionUnits,
  };

  return withProperGetter(requestMap[assetType] || getAssets, shouldFetchHeaders);
}

export function mapAssetByIdGetRequest(assetType) {
  switch (assetType) {
    case 'rig':
      return getRig;
    case 'well':
      return getWell;
    default:
      return getAsset;
  }
}

export const getAppMedia = (appId, queryParams) => {
  return get(`/v2/apps/${appId}/app_media`, queryParams);
};

export const deleteAppMedia = (appId, mediaId) => {
  return del(`/v2/apps/${appId}/app_media/${mediaId}`, null);
};

export const uploadAppMedia = (appId, { file, mediaType }) => {
  return sendFormData(`/v2/apps/${appId}/app_media`, {
    'app_media[media_type]': mediaType,
    'app_media[file]': file,
  });
};

export const updateAppMedia = (appId, mediaId, mediaFields) => {
  return patch(`/v2/apps/${appId}/app_media/${mediaId}`, { app_media: mediaFields });
};

export const getAppHelpContent = (appId, queryParams) => {
  return get(`/v2/apps/${appId}/app_help_contents`, queryParams);
};

export const deleteAppHelpContent = (appId, helpContentId) => {
  return del(`/v2/apps/${appId}/app_help_contents/${helpContentId}`, null);
};

export const uploadAppHelpContent = (appId, { title, description, url }) => {
  return sendFormData(`/v2/apps/${appId}/app_help_contents`, {
    'app_help_content[title]': title,
    'app_help_content[description]': description,
    'app_help_content[url]': url,
  });
};

export const updateAppHelpContent = (
  appId,
  helpContentId,
  { title, description, url, position }
) => {
  return patch(`/v2/apps/${appId}/app_help_contents/${helpContentId}`, {
    app_help_content: { title, description, url, position },
  });
};

export async function postAnnotation(annotation, options = {}) {
  return post('/v2/dashboard_app_annotations', {
    app_annotation: annotation,
    ...options,
  });
}

export async function patchAnnotation(annotationId, annotation) {
  return patch(`/v2/dashboard_app_annotations/${annotationId}`, {
    app_annotation: annotation,
  });
}

export async function deleteAnnotation(annotationId) {
  return del(`/v2/dashboard_app_annotations/${annotationId}`);
}

export async function getAnnotations(dashboardAppId, assetId, options = {}) {
  return get(`/v2/dashboard_app_annotations`, {
    app_id: dashboardAppId,
    asset_id: assetId,
    ...options,
  });
}

export async function getDashboardLastAnnotations(dashboardId, assetId, parentAssetId) {
  const params = { dashboard_id: dashboardId };
  if (assetId) params.asset_id = assetId;
  if (parentAssetId) params.parent_asset_id = parentAssetId;

  return get('/v2/dashboard_app_annotations/last_annotations', params);
}

export async function closeLastAnnotation(annotationId) {
  return patch(`/v2/dashboard_app_annotations/${annotationId}/close`);
}

export async function postAnnotationComment(annotationId, comment) {
  return post(`/v2/dashboard_app_annotations/${annotationId}/comments/`, comment);
}

export async function getAnnotationComments(annotationId, options = {}) {
  return get(`/v2/dashboard_app_annotations/${annotationId}/comments/`, options);
}

export function getFileDownloadLink(filename) {
  return `${baseUrl}/v1/file/download?file_name=${filename}`;
}

export async function patchFeedItemComment(feedItemId, commentId, comment) {
  return patch(`/v1/activities/${feedItemId}/comments/${commentId}`, comment);
}

export async function postFeedItemComment(feedItemId, comment) {
  return post(`/v1/activities/${feedItemId}/comments`, comment);
}

export async function deleteFeedItemComment(feedItemId, commentId) {
  return del(`/v1/activities/${feedItemId}/comments/${commentId}`);
}

export async function toggleFeedItemLike(feedItemId) {
  return post(`/v1/activities/${feedItemId}/likes/toggle`);
}

export async function patchFeedItem(feedItemId, feedItem) {
  return patch(`/v1/activities/${feedItemId}`, feedItem);
}

export async function deleteFeedItem(feedItemId) {
  return del(`/v1/activities/${feedItemId}`);
}

export async function getUserAutocomplete(type, value, companyId) {
  const queryParams = { type };

  if (value) {
    queryParams.search = value;
  }
  if (companyId) {
    queryParams.company_id = companyId;
  }

  return get('/v1/users/autocomplete', queryParams);
}

export async function getS3SignedUrl(filename, contentType) {
  return get(`/v1/file/sign?file_name=${encodeURIComponent(filename)}&contentType=${contentType}`);
}

export async function getS3DownloadLink(filename, contentDisposition = 'attachment') {
  return get(
    `/v1/file/download_link?file_name=${encodeURIComponent(filename)}&response_content_disposition=${contentDisposition}`, // prettier-ignore
    {}
  );
}

export const getAppPackages = (appId, queryParams) => {
  return get(`/v2/apps/${appId}/packages`, queryParams);
};

export async function getFracFleetWells(options) {
  return get('/v2/frac_fleets/wells', options);
}

export async function getPadWells(padId, options) {
  return get(`/v2/pads/${padId}/app_wells`, options);
}

export async function getTask(taskId) {
  return get(`/v2/tasks/${taskId}`, {});
}

export async function postTask(options = {}) {
  return post('/v2/tasks', options);
}

export const getAppSettingsTemplates = (appKey, params = {}) => {
  return get(`/v2/apps/${appKey}/app_settings_templates`, params);
};

export const getAppSettingsTemplate = (appKey, id) => {
  return get(`/v2/apps/${appKey}/app_settings_templates/${id}`, {});
};

export const patchAppSettingsTemplate = (appKey, id, { name, settings, metadata }) => {
  return patch(
    `/v2/apps/${appKey}/app_settings_templates/${id}`,
    {
      app_settings_template: {
        name,
        settings,
        metadata,
      },
    },
    {}
  );
};

export const postAppSettingsTemplate = (appKey, { name, settings, metadata }) => {
  return post(`/v2/apps/${appKey}/app_settings_templates`, {
    app_settings_template: {
      name,
      settings,
      metadata,
    },
  });
};

export const deleteAppSettingsTemplate = (appKey, id) => {
  return del(`/v2/apps/${appKey}/app_settings_templates/${id}`, {});
};

export const shareAppSettingsTemplate = (appKey, id, userIds) => {
  const query = queryString.stringify({ user_id: userIds }, { arrayFormat: 'bracket' });
  return post(`/v2/apps/${appKey}/app_settings_templates/${id}/share?${query}`, {});
};

export const unshareAppSettingsTemplate = (appKey, id, userIds) => {
  const query = queryString.stringify({ user_id: userIds }, { arrayFormat: 'bracket' });
  return post(`/v2/apps/${appKey}/app_settings_templates/${id}/unshare?${query}`, {});
};

export const publishAppSettingsTemplate = (appKey, templateId, { name, metadata }) => {
  return post(`/v2/apps/${appKey}/app_settings_templates/${templateId}/publish`, {
    name,
    metadata,
  });
};

export const getUsersWithHeaders = options => {
  const { page, perPage = 1000, sort, order, search, companyId, ids } = options;
  const queryParams = {
    page,
    per_page: perPage,
    sort,
    order,
    ids,
    search: search !== '' ? search : undefined,
    // NOTE: For Corva admins if there is no 'company_id', it returns all companies users
    company_id: companyId || undefined,
  };
  return getWithHeaders('/v1/users', queryParams);
};

export const getUsers = options => {
  const { page, perPage = 1000, sort, order, search, companyId, ids } = options;
  const queryParams = {
    page,
    per_page: perPage,
    sort,
    order,
    ids,
    search: search !== '' ? search : undefined,
    // NOTE: For Corva admins if there is no 'company_id', it returns all companies users
    company_id: companyId !== null ? companyId : undefined,
  };
  return get('/v1/users', queryParams);
};

export async function setCurrentUserSettings(userId, userSettings) {
  return post(`/v1/users/${userId}/settings`, userSettings);
}

export async function getUserSetting(userId, settingKey) {
  return get(`/v1/users/${userId}/settings/${settingKey}`);
}

export async function logout() {
  return post('/v1/logout');
}

export async function getPermissionCheck(options = {}) {
  return get(`/v2/ability_check/check_permission`, options, { isImmutable: false });
}

export async function getDashboards(params = {}) {
  return get('/v2/dashboards', params);
}

export function loginWithAuth0Token(platform, token) {
  return post('/v1/user_token', {
    auth: { token },
    platform,
  });
}

export async function getUserAuthSchemas(queryParams) {
  return get('/v1/sessions/schemas', queryParams);
}

export async function getUserAuthPlatform(platform) {
  return get(`/v1/sessions/platforms/${platform}`, null);
}

export async function postUserAuthTokenExchange(platform, code) {
  return post(`/v1/sessions/platforms/${platform}/token`, { code });
}
